// Strings of feature spec 17, phase 2 on the network page: the Settings tab's topology cards, Firewall section and
// impact panel, and the Overview's topologies (spec 11 6.15.12, 6.15.20, 6.15.27). `strings.networks.topology`.
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const networkTopology = {
  // Topology cards (6.15.12)
  fit: {
    isolated: 'Separate systems',
    mesh: 'A few systems',
    'hub-spoke': 'Many systems, central control',
    'micro-segmentation': 'Only requested traffic',
  } as Record<string, string>,
  sentence: {
    isolated: 'Separate address ranges, no connections between attachments.',
    mesh: 'Every attachment reaches every other directly.',
    'hub-spoke': 'A hub per region; spokes reach each other through the hub firewall, or not at all.',
    'micro-segmentation': 'Deny by default; only blueprint connections and approved requests pass.',
  } as Record<string, string>,
  noFixedCost: 'No fixed cost',
  fromCost: (amount: string) => `From about ${amount}/month per region with the firewall`,
  firewallAlreadyOn: 'No extra cost here (the firewall is already on)',
  applied: 'applied',
  availability: (providers: string) => `Available on ${providers}`,
  cardsLabel: 'Topology',
  microNote: (approved: number, pending: number) =>
    `With micro-segmentation, traffic between attachments passes only for approved requests (${approved} approved, ${pending} waiting for a decision). Blueprints declare requests; network admins decide them in the Traffic tab.`,
  spokeToSpoke: 'Spoke to spoke',
  viaHub: 'Through the hub firewall',
  viaHubCost: (amount: string) => `adds Azure Firewall, about ${amount}/month per region`,
  viaHubWithFirewall: 'through the firewall you turned on',
  blocked: 'Blocked',
  blockedCost: 'no extra cost',
  viaHubLater: 'needs the firewall: turn it on in Settings after creating the network',
  chooseSpokes: 'Choose how spokes reach each other',
  crossRegion: 'Across regions',
  connected: 'Connected across regions',
  separate: 'Separate',
  underneath: 'Connectivity underneath',
  underneathFirewall: (regions: string) => `Through the hub firewall (in ${regions})`,
  underneathDirect: 'Peered directly',
  underneathHelp: 'Without the firewall, micro-segmentation peers attachments directly.',
  // Firewall (6.15.12)
  firewall: 'Firewall',
  firewallSwitch: (provider: string) => `${provider} Firewall`,
  firewallOffHelp: 'Off: no hub firewall, no cost.',
  tier: 'Tier',
  tierOption: (tier: string, amount: string) => `${tier} · ${amount}/month per region`,
  listPrice: (asOf: string) => `list price, as of ${asOf}, without traffic`,
  egress: 'Internet traffic (egress)',
  egressDirect: 'Direct',
  egressDirectHelp: 'Apps reach the internet directly; nothing filters it.',
  egressFirewall: 'Through the firewall',
  egressFirewallHelp: 'Apps’ internet traffic goes through the hub firewall; only allowed host names pass.',
  allowedEgress: 'Allowed egress',
  allowedEgressHelp: 'Host names apps may reach, one per row; `*.` only as the first label.',
  addHost: 'Add host name',
  removeHost: (host: string) => `Remove ${host || 'host name'}`,
  hostLabel: (i: number) => `Allowed host name ${i}`,
  noFirewallKind: 'No firewall is available for this network’s providers.',
  firewallTurnedOnBy: 'Turned on by “Through the hub firewall”.',
  // Impact panel (6.15.12)
  impact: 'Impact',
  impactNote: 'dry run · no cloud calls',
  updating: 'Updating…',
  impactUnknown: 'Impact unknown',
  impactEmpty: 'Change the topology or the firewall to see what it does.',
  losesReachability: (n: number) => `Loses reachability (${plural(n, 'pair')})`,
  searchPairs: 'Search pairs',
  noPairsMatch: 'No pair matches.',
  noPairLoses: 'No pair loses reachability.',
  pendingRequests: (n: number) => plural(n, 'pending request'),
  noRequest: 'no request',
  viewRequests: 'View requests',
  viewRequestsFor: (a: string, b: string) => `View requests between ${a} and ${b}`,
  pairName: (a: string, b: string) => `${a} ↔ ${b}`,
  interruptions: 'Interruptions',
  noInterruptions: 'None.',
  costs: 'Costs and commitments',
  noCostChange: 'No change.',
  costLine: (sign: string, amount: string, item: string) => `${sign}${amount}/month · ${item}`,
  asOf: (asOf: string) => `list price, as of ${asOf}`,
  deploymentsToApply: 'Deployments to apply',
  none: 'None.',
  changedConnectivity: (groups: number) => `Gained or changed connectivity (${plural(groups, 'group')})`,
  changeGroup: (before: string, after: string, n: number) => `${before} → ${after} · ${plural(n, 'pair')}`,
  resources: 'Resources',
  resourceLine: (kind: string, create: number, remove: number) => `${kind}: +${create} −${remove}`,
  reachability: {
    none: 'none',
    direct: 'direct',
    'via-hub': 'via hub',
    'via-firewall': 'via firewall',
    'requests-only': 'requests only',
  } as Record<string, string>,
  viewImpact: 'View impact',
  // Overview topologies (6.15.20)
  hub: 'Hub',
  hubName: (region: string, cidr: string, firewall: string | null, blocked: boolean, linked: boolean) =>
    [
      `Hub of ${region}`,
      cidr,
      firewall ?? 'no firewall',
      ...(blocked ? ['no spoke-to-spoke'] : []),
      ...(linked ? ['connected to the other regions’ hubs'] : []),
    ].join(', '),
  firewallTier: (tier: string) => `Azure Firewall ${tier}`,
  noSpokeToSpoke: 'No spoke-to-spoke',
  meshEnclosure: 'Mesh: all reach each other directly',
  requestsOnly: 'requests only',
  connectedRegions: 'Connected regions',
  approvedRequests: (n: number) => plural(n, 'approved request'),
  legendTrunk: 'connected through the hub',
  legendFirewall: 'firewall',
  legendMesh: 'mesh',
  // Side panel and lists
  requests: 'Requests',
  noRequests: 'No requests.',
  requestOut: (requestId: string, target: string) => `${requestId} → ${target}`,
  requestIn: (requestId: string, source: string) => `${requestId} ← ${source}`,
  openTraffic: 'Open Traffic',
  requestsCount: (pending: number, enforcing: boolean) =>
    `${pending} pending${enforcing ? '' : ' · not enforced'}`,
  requestsInOut: (inbound: number, outbound: number) => `${inbound} in · ${outbound} out`,
  notEnforced: 'not enforced',
  // States and errors (6.15.27)
  switchToHubSpoke: 'Switch to hub-spoke',
  turnOnFirewall: 'Turn on the firewall or choose blocked',
};
